/**
 * `telegram/telegramSdk.ts` — the Telegram Mini App SDK, loaded on `/tg` only
 * (#2074).
 *
 * Telegram opens the Mini App with its launch data in the URL hash
 * (`#tgWebAppData=…&tgWebAppThemeParams=…&tgWebAppStartParam=…`); the
 * official script (`telegram-web-app.js`) reads it into `window.Telegram.
 * WebApp` and talks to the Telegram client (ready, expand, the Back button,
 * theme changes). This module injects that script once, waits a few seconds
 * for it, and — so the page still works if the script is slow or blocked —
 * reads the launch data straight from the hash captured at boot.
 *
 * Nothing of ours is stored: the signed `initData` is posted to the session
 * route once and dropped. (The SDK keeps its own copy of the launch data in
 * `sessionStorage`, `__telegram__initParams`; when the script comes late we
 * put the captured hash there first — see `keepLaunchForSdk`.)
 *
 * #2172 M2 (founder's real-phone check, 12 Oct 2026) — Telegram's own
 * swipe-down-to-collapse is turned off for the whole Mini App the moment the
 * SDK is there (`startTelegramChrome`): the Calendar pages sideways and the
 * sheets pull down, and Telegram's gesture held the finger's moves back and
 * then took the drag (the app dropped to half height and minimised).
 */

export const TELEGRAM_SDK_SRC = "https://telegram.org/js/telegram-web-app.js";
const SDK_TIMEOUT_MS = 4000;

/** Every colour Telegram hands a Mini App (Bot API 8.0); all optional. */
export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  bottom_bar_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  section_separator_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

/** Telegram's insets in CSS pixels: the device's (notch, home indicator) and,
 *  in full screen, the band its own Close / ⋯ buttons float in. */
export interface TelegramInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface TelegramBackButton {
  show: () => void;
  hide: () => void;
  onClick: (fn: () => void) => void;
  offClick: (fn: () => void) => void;
}

/** "Settings" in Telegram's own ⋯ menu (Bot API 7.0). */
interface TelegramSettingsButton {
  show: () => void;
  hide: () => void;
  onClick: (fn: () => void) => void;
  offClick: (fn: () => void) => void;
}

/** The phone's haptic engine through Telegram (Bot API 6.1). */
export interface TelegramHapticFeedback {
  impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
  notificationOccurred: (type: "error" | "success" | "warning") => void;
  selectionChanged: () => void;
}

/** The slice of `window.Telegram.WebApp` Tarii uses. */
export interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { start_param?: string };
  version?: string;
  platform?: string;
  colorScheme?: "light" | "dark";
  themeParams?: TelegramThemeParams;
  safeAreaInset?: TelegramInsets;
  contentSafeAreaInset?: TelegramInsets;
  /** Full screen (Bot API 8.0): Telegram floats its Close / ⌄ ⋯ over the page. */
  isFullscreen?: boolean;
  /** Whether a downward swipe may collapse the Mini App (Bot API 7.7). */
  isVerticalSwipesEnabled?: boolean;
  enableVerticalSwipes?: () => void;
  disableVerticalSwipes?: () => void;
  HapticFeedback?: TelegramHapticFeedback;
  ready: () => void;
  expand: () => void;
  requestFullscreen?: () => void;
  isVersionAtLeast?: (version: string) => boolean;
  onEvent?: (event: string, fn: () => void) => void;
  offEvent?: (event: string, fn: () => void) => void;
  openLink?: (url: string) => void;
  openTelegramLink?: (url: string) => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  BackButton?: TelegramBackButton;
  SettingsButton?: TelegramSettingsButton;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/** True on the Mini App's own address: `/tg` and everything under it. */
export function isMiniAppPath(pathname: string): boolean {
  return pathname === "/tg" || pathname.startsWith("/tg/");
}

// The hash and query as Telegram opened the page — captured before the
// router's first redirect drops them.
interface LaunchParams {
  initData: string | null;
  startParam: string | null;
  themeParams: TelegramThemeParams | null;
  colorScheme: "light" | "dark" | null;
  /** Every `tgWebApp…` hash parameter as Telegram sent it (decoded once). */
  sdkParams: Record<string, string>;
}

let launch: LaunchParams | null = null;

function parseLaunch(): LaunchParams {
  if (typeof window === "undefined") {
    return {
      initData: null,
      startParam: null,
      themeParams: null,
      colorScheme: null,
      sdkParams: {},
    };
  }
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const sdkParams: Record<string, string> = {};
  for (const [key, value] of hash) {
    if (key.startsWith("tgWebApp")) sdkParams[key] = value;
  }
  const query = new URLSearchParams(window.location.search);
  let themeParams: TelegramThemeParams | null = null;
  const rawTheme = hash.get("tgWebAppThemeParams");
  if (rawTheme) {
    try {
      themeParams = JSON.parse(rawTheme) as TelegramThemeParams;
    } catch {
      themeParams = null;
    }
  }
  return {
    initData: hash.get("tgWebAppData"),
    startParam: hash.get("tgWebAppStartParam") ?? query.get("startapp"),
    themeParams,
    colorScheme: null,
    sdkParams,
  };
}

/** Where `telegram-web-app.js` keeps its launch data between loads. */
const SDK_PARAMS_KEY = "__telegram__initParams";

/**
 * The SDK reads its launch data (version, platform, `initData`) from the
 * hash when it RUNS. If it comes late — after the session started without
 * it and the router's first redirect dropped the hash — it would wake up as
 * version 6.0 on platform "unknown" and refuse everything (haptics, full
 * screen, `disableVerticalSwipes`). It also reads its own `sessionStorage`
 * copy for anything the hash lacks, so put what Telegram opened THIS page
 * with there first. This launch wins over whatever an earlier launch left
 * in the same tab (Telegram Web / Desktop re-opening the app in it) — as the
 * SDK's own hash-over-storage merge does — so a late script never signs in
 * with an old, expired `initData`.
 */
function keepLaunchForSdk(): void {
  const params = launchParams().sdkParams;
  if (Object.keys(params).length === 0) return;
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(SDK_PARAMS_KEY) ?? "null") as Record<
      string,
      string
    > | null;
    window.sessionStorage.setItem(SDK_PARAMS_KEY, JSON.stringify({ ...(stored ?? {}), ...params }));
  } catch {
    // No storage (a private window): the hash is all the SDK gets.
  }
}

/** The launch data from the URL Telegram opened (read once, then kept). */
export function launchParams(): LaunchParams {
  if (!launch) launch = parseLaunch();
  return launch;
}

/** Tests only: forget the captured launch data. */
export function resetLaunchParamsForTests(): void {
  launch = null;
  sdkPromise = null;
  lateArrivals.clear();
}

let sdkPromise: Promise<TelegramWebApp | null> | null = null;
/** Waiting for a script that missed `SDK_TIMEOUT_MS` but may still arrive. */
const lateArrivals = new Set<(app: TelegramWebApp) => void>();

/** The SDK's `WebApp`, if the script has loaded. */
export function telegramWebApp(): TelegramWebApp | null {
  if (typeof window === "undefined") return null;
  return window.Telegram?.WebApp ?? null;
}

/** Inject `telegram-web-app.js` once; resolve to `WebApp`, or `null` after a few seconds. */
export function loadTelegramSdk(timeoutMs = SDK_TIMEOUT_MS): Promise<TelegramWebApp | null> {
  launchParams();
  const existing = telegramWebApp();
  if (existing) return Promise.resolve(existing);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(null);
      return;
    }
    const done = () => resolve(telegramWebApp());
    const timer = window.setTimeout(done, timeoutMs);
    keepLaunchForSdk();
    const script = document.createElement("script");
    script.src = TELEGRAM_SDK_SRC;
    script.async = true;
    script.onload = () => {
      window.clearTimeout(timer);
      done();
      // Later than the wait: whoever is still waiting gets it now.
      const app = telegramWebApp();
      if (!app) return;
      for (const fn of [...lateArrivals]) {
        lateArrivals.delete(fn);
        fn(app);
      }
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      resolve(null);
      // Nothing will arrive: let go of whoever was waiting for it.
      lateArrivals.clear();
    };
    document.head.appendChild(script);
  });
  return sdkPromise;
}

/**
 * Run `fn` with the SDK's `WebApp` as soon as it is there (#2176): at once if
 * the script has loaded, else when `loadTelegramSdk()` settles (the same
 * cached promise — the script is injected once), else when a script that
 * missed the wait arrives after all. Never called when the script fails.
 * Returns a cancel for an unmount that comes first.
 *
 * Why: the Mini App's hooks mount before `AuthContext` loads the script
 * (a child's effects run before its provider's), so a mount effect that reads
 * `telegramWebApp()` directly sees nothing and never subscribes.
 */
export function whenTelegramWebApp(fn: (app: TelegramWebApp) => void): () => void {
  const now = telegramWebApp();
  if (now) {
    fn(now);
    return () => {};
  }
  let live = true;
  const late = (app: TelegramWebApp) => {
    if (live) fn(app);
  };
  void loadTelegramSdk().then((app) => {
    if (!live) return;
    if (app) fn(app);
    else lateArrivals.add(late);
  });
  return () => {
    live = false;
    lateArrivals.delete(late);
  };
}

/**
 * Turn Telegram's own swipe-down-to-collapse off (Bot API 7.7) — for the
 * whole Mini App: the Calendar's pages swipe sideways, sheets pull down, and
 * a drag that drifts down must never collapse or minimise the app. ✕ Close
 * and Telegram's header still close and minimise it. Nothing on an older
 * client or outside a Telegram client (`platform: "unknown"`).
 */
export function disableTelegramVerticalSwipes(app: TelegramWebApp | null): boolean {
  if (!app?.disableVerticalSwipes || !app.platform || app.platform === "unknown") return false;
  if (!(app.isVersionAtLeast?.("7.7") ?? false)) return false;
  // Already off (the app turned it off at start): nothing to post again.
  if (app.isVerticalSwipesEnabled === false) return true;
  try {
    app.disableVerticalSwipes();
    return true;
  } catch {
    return false;
  }
}

/**
 * Tell Telegram the page is ready; open it full height (and full screen on
 * phones); turn its swipe-down-to-collapse off for the whole session. Once
 * per page load (`AuthContext`), never in a loop.
 */
export function startTelegramChrome(app: TelegramWebApp | null): void {
  if (!app) return;
  try {
    app.ready();
    app.expand();
    const phone = app.platform === "ios" || app.platform === "android";
    if (phone && app.requestFullscreen && (app.isVersionAtLeast?.("8.0") ?? false)) {
      app.requestFullscreen();
    }
  } catch {
    // An older client without one of these: the page still works.
  }
  disableTelegramVerticalSwipes(app);
}

/** The signed `initData` — from the SDK, else from the hash. */
export function readInitData(app: TelegramWebApp | null): string | null {
  const fromSdk = app?.initData;
  if (fromSdk) return fromSdk;
  return launchParams().initData;
}

/** The `startapp` key the Mini App was opened with, if any. */
export function readStartParam(app: TelegramWebApp | null = telegramWebApp()): string | null {
  return app?.initDataUnsafe?.start_param || launchParams().startParam;
}

/** Open a page in the member's own browser — out of Telegram's webview (the
 *  web sign-in cannot run inside it). Plain new tab outside Telegram. */
export function openOutsideTelegram(
  url: string,
  app: TelegramWebApp | null = telegramWebApp(),
): void {
  if (app?.openLink) {
    try {
      app.openLink(url);
      return;
    } catch {
      // Fall through to a plain new tab.
    }
  }
  if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * #2071 S2 — share a link the way the phone does it. Inside Telegram, its own
 * share list (`openTelegramLink("https://t.me/share/url?url=…&text=…")`); in
 * a phone browser, the share sheet (`navigator.share`); otherwise, copy it.
 * Resolves to what happened, so the caller can say *Copied*.
 */
export type ShareOutcome = "telegram" | "shared" | "copied" | "failed";

export function telegramShareUrl(url: string, text: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
}

export async function shareLink(
  url: string,
  text: string,
  app: TelegramWebApp | null = telegramWebApp(),
): Promise<ShareOutcome> {
  if (app?.openTelegramLink) {
    try {
      app.openTelegramLink(telegramShareUrl(url, text));
      return "telegram";
    } catch {
      // An older client: fall through to the browser's ways.
    }
  }
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ title: "Tarii", text, url });
      return "shared";
    } catch (err) {
      // The person closed the sheet: nothing to do, nothing went wrong.
      if (err instanceof DOMException && err.name === "AbortError") return "shared";
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch {
    return "failed";
  }
}
